import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AddIcon, ChevronDownIcon, ChevronUpIcon, CloseIcon } from "@/lib/icons";
import { TOPIC_NAME_NEWLINES, type EditableTopicRow } from "@/lib/inspectionTopicRows";

/**
 * The editable tarkastuskohdat row list — textareas plus up/down/delete and
 * "Lisää tarkastuskohta". Shared by the standalone tarkastuskohdat editor and
 * the tarkastuskohdat section embedded in Muokkaa tehtävän tietoja.
 */
export function InspectionTopicRowList({
  rows,
  onUpdateRow,
  onRemoveRow,
  onMoveRow,
  onAddRow,
  disabled,
}: {
  rows: EditableTopicRow[];
  onUpdateRow: (key: string, name: string) => void;
  onRemoveRow: (key: string) => void;
  onMoveRow: (index: number, direction: -1 | 1) => void;
  onAddRow: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-2 min-w-0">
      {rows.map((row, index) => (
        <div key={row.key} className="flex items-start gap-2 min-w-0">
          <Textarea
            value={row.name}
            // Topic names are single-line in Congrid; the two rows are
            // for reading long ones, so newlines collapse to spaces.
            onChange={(e) =>
              onUpdateRow(row.key, e.target.value.replace(TOPIC_NAME_NEWLINES, " "))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            rows={2}
            placeholder="Tarkastuskohdan nimi"
            // The shared Textarea's min-h-[80px] is far taller than two
            // lines need; this holds exactly two and scrolls past that.
            className="no-scrollbar w-full max-w-full box-border resize-none text-sm leading-snug min-h-0 h-[52px] py-1.5"
            disabled={disabled}
          />
          <div className="flex shrink-0 items-center gap-0.5 pt-1">
            <button
              type="button"
              onClick={() => onMoveRow(index, -1)}
              disabled={disabled || index === 0}
              title="Siirrä ylemmäs"
              aria-label={`Siirrä ${row.name || "rivi"} ylemmäs`}
              className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent disabled:opacity-30 disabled:pointer-events-none"
            >
              <ChevronUpIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onMoveRow(index, 1)}
              disabled={disabled || index === rows.length - 1}
              title="Siirrä alemmas"
              aria-label={`Siirrä ${row.name || "rivi"} alemmas`}
              className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent disabled:opacity-30 disabled:pointer-events-none"
            >
              <ChevronDownIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onRemoveRow(row.key)}
              disabled={disabled}
              title="Poista tarkastuskohta"
              aria-label={`Poista ${row.name || "rivi"}`}
              className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:pointer-events-none"
            >
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onAddRow}
        disabled={disabled}
        className="w-full justify-start text-muted-foreground hover:text-foreground mt-1"
      >
        <AddIcon className="h-4 w-4 mr-1" />
        Lisää tarkastuskohta
      </Button>
    </div>
  );
}
